import {useI18n} from 'i18n/I18nContext'
import React from 'react'
import {useEditor} from 'TemplateEditor/EditorProvider'
import {useFormicForm} from 'TemplateEditor/Layout/Blocks'
import {BorderInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/BorderInput'
import {PaddingInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/PaddingInput'
import {
  Border,
  Padding,
  Position,
  StyledTitle,
} from 'TemplateEditor/Layout/Blocks/DefaultBlockConfig'
import SmartphoneIcon from '@mui/icons-material/Smartphone'
import LaptopMacIcon from '@mui/icons-material/LaptopMac'
import {PositionInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/PositionInput'
import {ColorInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/ColorInput'
import {ImageInput} from 'TemplateEditor/Layout/Blocks/ConfigInputs/ImageInput'
import {TEMPLATE_EDITOR_ACTIVE_FORM_ID} from 'TemplateEditor/templateEditorFormId'
import {EditorConfigTopBar} from 'TemplateEditor/EditorConfigTopBar'
import {humanColumnNumberFromEditKey} from 'TemplateEditor/editorPanelTitles'

export type SubSectionConfigType = {
  position: Position
  padding: Padding
  border: Border
  background: string | null
  backgroundImage: string | null
}

export const subSectionDefauts = (): SubSectionConfigType => {
  return {
    position: {
      vertical: 'center',
      horizontal: 'center',
    },
    padding: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
    border: {
      color: null,
      width: 0,
      radius: 0,
    },
    background: null,
    backgroundImage: null,
  }
}

export function SubSectionConfig() {
  const {t} = useI18n()
  const {setEditElement, editElement, mobileMode, setMobileMode} = useEditor()

  const handleToggleMobileMode = () => setMobileMode(!mobileMode)

  const {handleSubmit, values, setFieldValue} =
    useFormicForm<SubSectionConfigType>()

  const colNum = editElement ? humanColumnNumberFromEditKey(editElement) : null
  const columnTitle =
    colNum != null
      ? `${t('templateEditor.columnLabel')} ${colNum}`
      : t('templateEditor.columnLabel')

  return (
    <>
      <div className={'offcanvas__editor info-open'}>
        <div className="offcanvas__wrapper">
          <div className="offcanvas__content template-editor-config-split">
            <div className="template-editor-config-sticky">
              <EditorConfigTopBar
                itemLabel={columnTitle}
                backLabel={t('templateEditor.backToLayout')}
                onBack={() => setEditElement(null)}
              />

              <div className={`bd-theme-toggle mb-20 d-block`}>
                <label
                  className="bd-theme-toggle-main"
                  style={{width: '100%'}}
                  htmlFor="template-editor-config-mobile-toggle"
                >
                  <span
                    className={`bd-theme-toggle-dark ${!mobileMode ? 'active' : ''}`}
                  >
                    <LaptopMacIcon style={{fontSize: 18}} />{' '}
                    {t('templateEditor.sidebarDesktop')}
                  </span>
                  <input
                    type="checkbox"
                    id="template-editor-config-mobile-toggle"
                    checked={!mobileMode}
                    onChange={handleToggleMobileMode}
                  />
                  <i className="bd-theme-toggle-slide"></i>
                  <span
                    className={`bd-theme-toggle-light ${mobileMode ? 'active' : ''}`}
                  >
                    <SmartphoneIcon style={{fontSize: 18}} />{' '}
                    {t('templateEditor.sidebarMobile')}
                  </span>
                </label>
              </div>
            </div>

            <div className="template-editor-config-scroll">
              <form id={TEMPLATE_EDITOR_ACTIVE_FORM_ID} onSubmit={handleSubmit}>
                <ColorInput
                  value={values.background ?? '#ffffff'}
                  name={'background'}
                  label={t('templateEditor.backgroundColorLabel')}
                  setFieldValue={setFieldValue}
                />

                <ImageInput
                  value={values.backgroundImage}
                  name={'backgroundImage'}
                  label={t('templateEditor.backgroundImageLabel')}
                  setFieldValue={setFieldValue}
                />

                <StyledTitle>{t('templateEditor.contentTitle')}</StyledTitle>

                <PositionInput
                  value={values.position}
                  name={'position'}
                  label={t('templateEditor.positionLabel')}
                  setFieldValue={setFieldValue}
                />

                <StyledTitle>{t('templateEditor.bordersTitle')}</StyledTitle>

                <BorderInput
                  value={values.border}
                  name={'border'}
                  label={t('templateEditor.bordersFieldLabel')}
                  setFieldValue={setFieldValue}
                />

                <StyledTitle>{t('templateEditor.paddingTitle')}</StyledTitle>

                <PaddingInput
                  value={values.padding}
                  name={'padding'}
                  label={t('templateEditor.paddingFieldLabel')}
                  setFieldValue={setFieldValue}
                  max={500}
                  min={0}
                />
              </form>
            </div>

            <div className="template-editor-config-footer">
              <button
                className="input__btn w-100 template-editor-save-submit"
                type="submit"
                form={TEMPLATE_EDITOR_ACTIVE_FORM_ID}
              >
                {t('templateEditor.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
